import { FC, useEffect, useState, useMemo } from 'react';
import Constraints from 'src/shared-components/Constraints/Constraints';
import { FormattedMessage } from 'react-intl';
import {
  AccordionHeaderContent,
  AccordionItemContentType,
  AllAnalogues,
  DrugItem,
  DrugReview,
  PriceHistory
} from 'src/api/types';
import { Tab } from 'src/shared-components/Tabs/Tabs';
import { useRouter } from 'next/router';
import { containerWithBackgroundImageStyle } from 'src/styles/shared';
import dayjs from 'dayjs';
import cn from 'classnames';
import useGTAG, { GTAGPrefix } from 'src/hooks/useGTAG';
import useOneDrugOrderStore from 'src/hooks/useOneDrugOrderStore';
import { useAnalytics } from 'src/hooks/useAnalytics/useAnalytics';
import { useIsMounted } from 'src/hooks/useIsMounted';
import Skeleton from 'react-loading-skeleton';
import dynamic from 'next/dynamic';
import { delayWrapper } from 'src/utils/delayWrapper';
import trns from '../trns';
import { TabTypes, generateFAQList } from '../utils';
import { generateDrugPageLink } from '../../../utils/links';
import useCartStore from '../../../hooks/useCart';

const Reviews = dynamic(
  () => delayWrapper<typeof Reviews>(import('./ReviewsComponents/Reviews')),
  {
    loading: () => <Skeleton width={'100%'} height={24} />
  }
);

const AboutProduct = dynamic(
  () => delayWrapper<typeof AboutProduct>(import('./TabContent/AboutProduct')),
  {
    loading: () => <Skeleton width={'100%'} height={300} />
  }
);

const Tabs = dynamic(
  () => delayWrapper<typeof Tabs>(import('src/shared-components/Tabs/Tabs')),
  {
    loading: () => <Skeleton width={'100%'} height={120} />
  }
);

const Accordion = dynamic(
  () =>
    delayWrapper<typeof Accordion>(
      import('src/shared-components/Accordion/Accordion')
    ),
  {
    loading: () => <Skeleton width={'100%'} height={600} />
  }
);

const AccordionDefaultBody = dynamic(
  () =>
    delayWrapper<typeof AccordionDefaultBody>(
      import('src/shared-components/Accordion/AccordionDefaultBody')
    ),
  {
    loading: () => <Skeleton width={'100%'} height={64} />
  }
);

const AccordionDefaultHeader = dynamic(
  () =>
    delayWrapper<typeof AccordionDefaultHeader>(
      import('src/shared-components/Accordion/AccordionDefaultHeader')
    ),
  {
    loading: () => <Skeleton width={'100%'} height={64} />
  }
);

const ReviewsComponent = dynamic(
  () =>
    delayWrapper<typeof ReviewsComponent>(
      import('../../../shared-components/Reviews/ReviewsComponent')
    ),
  {
    loading: () => <Skeleton width={'100%'} height={600} />
  }
);

// const ViewedDrugsContainer = dynamic(
//   () =>
//     delayWrapper<typeof ViewedDrugsContainer>(
//       import('../../../shared-components/ViewedDrugs/ViewedDrugsContainer')
//     ),
//   {
//     loading: () => <Skeleton width={'100%'} height={120} />
//   }
// );

// const PopularDrugsContainer = dynamic(
//   () =>
//     delayWrapper<typeof PopularDrugsContainer>(
//       import('src/shared-components/Tabs/Tabs')
//     ),
//   {
//     loading: () => <Skeleton width={'100%'} height={120} />
//   }
// );

interface Props {
  drug: DrugItem;
  analoguesList: AllAnalogues | null;
  // otherPopularDrugsList: DrugItem[] | null;
  thirdCategoryName: string;
  priceHistory: PriceHistory[];
  reviewsList: DrugReview[];
}

const DrugInfoTabs: FC<Props> = ({
  drug,
  thirdCategoryName,
  analoguesList,
  // otherPopularDrugsList,
  priceHistory,
  reviewsList
}) => {
  const router = useRouter();
  const { isMounted } = useIsMounted();

  const { addItemToSingleDrugCart } = useCartStore();
  const { sendChangeCartItemsAnalytic } = useAnalytics();
  const { setOneDrugOrder, setBackRoute } = useOneDrugOrderStore();
  const { trackGtagEvent } = useGTAG();

  const [quantity, setQuantity] = useState({
    packQuantity: 1,
    unitQuantity: 0
  });

  const translitName = router.query?.translitName as string;
  const drugPageLink = generateDrugPageLink({
    translitName
  });

  useEffect(() => {
    setQuantity({
      packQuantity: 1,
      unitQuantity: 0
    });
  }, [drug.translitName]);

  const aboutTab = {
    id: TabTypes.ABOUT_PRODUCT,
    title: <FormattedMessage {...trns.aboutProduct} />,
    options: {
      href: drugPageLink.pathname,
      shallow: true
    },
    gtag: 'about'
  };

  const instructionsTab = {
    id: TabTypes.INSTRUCTIONS,
    title: <FormattedMessage {...trns.instructionsTab} />,
    options: {
      href: `${drugPageLink.pathname}#${TabTypes.INSTRUCTIONS}`,
      scroll: false,
      shallow: true
    },
    gtag: 'instruction'
  };

  const pricesTab = {
    id: TabTypes.PRICES,
    title: <FormattedMessage {...trns.pricesTab} />,
    gtag: 'prices'
  };

  const reviewsTab = {
    id: TabTypes.REVIEWS,
    title: (
      <FormattedMessage
        {...trns.reviewsTab}
        values={{
          rates: <span>{drug?.rateCount}</span>
        }}
      />
    ),
    options: {
      href: `${drugPageLink.pathname}#${TabTypes.REVIEWS}`,
      scroll: false,
      shallow: true
    },
    gtag: 'review'
  };

  const analogsTab = {
    id: TabTypes.ANALOGUE,
    title: <FormattedMessage {...trns.analogTab} />,
    options: {
      // TODO: add shallow redirect & load analytics on client side
      href: `${drugPageLink.pathname}/analogi`,
      scroll: false
    },
    gtag: 'analogues'
  };

  const tabList = [
    aboutTab,
    instructionsTab,
    pricesTab,
    drug.withAnalogs ? analogsTab : undefined,
    reviewsTab
  ].filter(Boolean);

  const preSelectedTab = useMemo(
    () =>
      tabList?.find(
        (tab) =>
          tab.id === router.query?.tab?.[0] || tab.id === router.query?.tab
      ),
    [router.query]
  );

  const [selectedTabState, setSelectedTab] = useState<Tab>(preSelectedTab);

  const selectedTab = selectedTabState ?? aboutTab;

  useEffect(() => {
    if (router.query.translitName) {
      const tabToSelect =
        analoguesList?.fullAnalogue.length > 0 && !selectedTabState
          ? analogsTab
          : preSelectedTab;
      setSelectedTab(tabToSelect);
    }
  }, [router.query.translitName]);

  useEffect(() => {
    if (preSelectedTab && preSelectedTab.id !== TabTypes.ANALOGUE) {
      // ANALOGUE is the only preselected tab allowed for now.
      // Throw error in order to prevent bad link to be created and crawled by google bots
      throw new Error('ANALOGUE is the only preselected tab allowed for now');
    } else if (
      TabTypes.ANALOGUE === preSelectedTab?.id &&
      TabTypes.ANALOGUE !== selectedTab.id
    ) {
      setSelectedTab(preSelectedTab);
    }
  }, [preSelectedTab?.id]);

  const handleTabSwitch = (tabItem) => {
    if (tabItem.id === TabTypes.PRICES) {
      router.push('/checkout/cart?step=proposalSelect', undefined, {
        shallow: true
      });
      addItemToSingleDrugCart({
        drug,
        packQuantity: quantity.packQuantity,
        unitQuantity: quantity.unitQuantity,
        gtagPrefix: GTAGPrefix.item
      });
      setTimeout(() => {
        sendChangeCartItemsAnalytic({
          drug,
          packageQuantityDetails: {
            ...quantity,
            quantityUnitInPack: drug.quantityUnitInPack
          }
        });
        trackGtagEvent(`${GTAGPrefix.item}_pharmacy_choose`);
        setBackRoute(router.asPath);
        setOneDrugOrder(true);
      });
    } else {
      setSelectedTab(tabItem);
    }
  };

  const popularQuestionsNavigationLinks = generateFAQList(drug);
  const isAnaloguesTab = TabTypes.ANALOGUE === selectedTab.id;

  return (
    <div
      style={containerWithBackgroundImageStyle}
      className="flex w-full relative"
    >
      <Constraints className="w-full pt-[10px] m:pt-[85px] pb-10">
        <div className="flex flex-col">
          <div className="text-darkBlue text-[20px] fs-body-bold px-[18px] mb-[8px] lm:px-0">
            <h1>
              {isAnaloguesTab ? (
                <FormattedMessage
                  values={{ tradeName: drug.name }}
                  {...trns.analogueTitle}
                />
              ) : (
                drug.name
              )}
            </h1>
          </div>
          <div className="px-[18px] mb-[24px] m:mb-[33px] lm:px-0">
            <Reviews
              averageRate={drug?.averageRate}
              rateCount={drug?.rateCount}
              isOnDrugPage
              drugPageURL={drugPageLink.pathname}
              gtagPrefix={GTAGPrefix.item}
            />
          </div>
        </div>
        <div className="px-[18px] lm:px-0">
          <Tabs
            tabList={tabList}
            forceSelectedTab={selectedTab}
            onSwitch={handleTabSwitch}
            gtagPrefix={GTAGPrefix.item}
          />
        </div>
        <div className="relative bg-white flex w-full z-10 s:py-2.5 m:py-9 ">
          <div className="w-full">
            <AboutProduct
              drug={drug}
              analoguesList={analoguesList}
              thirdCategoryName={thirdCategoryName}
              tabId={selectedTab.id as TabTypes}
              gtagPrefix={GTAGPrefix.item}
            />
          </div>
        </div>
        {/* These carousels temporarily removed for INP perfomance testing */}
        {/* {selectedTab.id !== TabTypes.ANALOGUE && (
          <>
            <div className="lm:mb-16 mb-0 lm:mx-0 mx-4">
              <ViewedDrugsContainer />
            </div>
            <div className="lm:mb-16 mb-0 lm:mx-0 mx-4">
              <PopularDrugsContainer
                drugsList={otherPopularDrugsList}
                isLoading={false}
              />
            </div>
          </>
        )} */}
        {priceHistory?.length > 0 && !isAnaloguesTab && (
          <div className={cn(isMounted && 'hidden')}>
            <table>
              <tbody>
                <tr>
                  <th>Дата</th>
                  <th>Ціна</th>
                </tr>
                {priceHistory.map((item, index) => {
                  return (
                    <tr key={index}>
                      <td>{dayjs(item.date).format('DD-MM-YYYY')}</td>
                      <td>{item.value}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!isAnaloguesTab && (
          <>
            <div className="px-8 mt-12 mb-30 m:mb-0">
              <p className="fs-header5-bold mb-7">
                <FormattedMessage {...trns.askedQuestions} />
              </p>
              <Accordion
                accordionList={popularQuestionsNavigationLinks.map((group) => ({
                  header: {
                    id: group.id,
                    title: group.name,
                    useCustomWrapper: (titleChild: string) => (
                      <h3>{titleChild}</h3>
                    )
                  },
                  body: [
                    <div
                      key={group?.id}
                      className={cn(
                        'fs-caption-reg text-tundora compendium-innerHtmlStyling bg-white rounded-b-xl',
                        'xs:w-1/2 sm:w-2/3 xs:rounded-b-0 xs:pb-0 xs:pr-0'
                      )}
                    >
                      {group.bodyText}
                    </div>
                  ]
                }))}
                renderItem={(content) => (
                  <AccordionDefaultBody content={content} />
                )}
                classNameButton={'pt-3.5 pb-3.5 border-b border-periwinkleGray'}
                renderHeader={(
                  header: AccordionHeaderContent,
                  productsList: AccordionItemContentType[],
                  active: false
                ) => (
                  <AccordionDefaultHeader
                    header={header}
                    productsList={productsList}
                    active={active}
                  />
                )}
              />
            </div>
            <div id={TabTypes.REVIEWS} className="px-8 mt-12 mb-30 m:mb-0">
              <ReviewsComponent
                drugTranslitname={drug?.translitName}
                drugName={drug?.name}
                averageRate={drug?.averageRate}
                rateCount={drug?.rateCount}
                reviews={reviewsList}
                gtagPrefix={GTAGPrefix.item}
              />
            </div>
          </>
        )}
      </Constraints>
    </div>
  );
};

export default DrugInfoTabs;
